'use client';

import { useCallback, useRef, useState } from 'react';
import { CLIENTS_IMPORT_CSV_ONLY } from '@/app/(main)/clients/const/clients';
import { useClientsFileImport } from '@/app/(main)/clients/hooks/useClientsFileImport';
import { DropZoneArea } from '@/app/components/DropZoneArea';
import { Modal } from '@/app/components/Modal';
import { ModalMobileHeader } from '@/app/components/Modal/ModalMobileHeader';
import { BaseButton } from '@/app/components/buttons/BaseButton';
import { IconButton } from '@/app/components/buttons/IconButton';
import { AgentSelector } from '@/app/components/form/AgentSelector';
import { Selector } from '@/app/components/form/Selector';
import type { SelectorOption } from '@/app/components/form/Selector/types';
import { useFileDrop } from '@/hooks/useFileDrop';
import type { ErrorResponse } from '@/types/apiTypes';
import {
  IMPORT_SOURCE_OPTIONS,
  IMPORT_TYPE_OPTIONS,
  type ImportClientSource,
  type ImportClientType,
  type ImportPrimaryClientsData,
} from '@/types/clientDetailsApiTypes';
import { getParsedError } from '@/utils/getParsedError';
import { cn } from '@/utils/сn';
import { ResultsStep } from './components/ResultsStep';
import FileIcon from '/public/icons/file.svg';
import TrashIcon from '/public/icons/trash.svg';
import UploadCloudIcon from '/public/icons/upload-cloud.svg';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

interface DropZoneHintInput {
  isAllowedExtensionError: boolean;
  isMaxFileSizeError: boolean;
  uploadErrorMessage: string | null;
  acceptedTypesLabel: string;
}

interface DropZoneHint {
  text: string;
  isError: boolean;
}

const getDropZoneHint = ({
  isAllowedExtensionError,
  isMaxFileSizeError,
  uploadErrorMessage,
  acceptedTypesLabel,
}: DropZoneHintInput): DropZoneHint => {
  if (isAllowedExtensionError) {
    return { text: `Please upload ${acceptedTypesLabel} files only.`, isError: true };
  }

  if (isMaxFileSizeError) {
    return { text: 'File is too large. Maximum size is 10 MB per file.', isError: true };
  }

  if (uploadErrorMessage) {
    return { text: uploadErrorMessage, isError: true };
  }

  return { text: `${acceptedTypesLabel}\u00A0·\u00A0Max 1 file\u00A0·\u00A0Max 10 MB per file`, isError: false };
};

interface ImportClientsFileModalProps {
  isOpen: boolean;
  onClose: () => void;
  accept?: string;
  acceptedExtensions?: string[];
  acceptedTypesLabel?: string;
}

export const ImportClientsFileModal = ({
  isOpen,
  onClose,
  accept = CLIENTS_IMPORT_CSV_ONLY.accept,
  acceptedExtensions = CLIENTS_IMPORT_CSV_ONLY.extensions,
  acceptedTypesLabel = CLIENTS_IMPORT_CSV_ONLY.label,
}: ImportClientsFileModalProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [selectedAgent, setSelectedAgent] = useState<SelectorOption<string> | null>(null);
  const [importType, setImportType] = useState<SelectorOption<ImportClientType>>(IMPORT_TYPE_OPTIONS[0]);
  const [importSource, setImportSource] = useState<SelectorOption<ImportClientSource>>(IMPORT_SOURCE_OPTIONS[0]);
  const [results, setResults] = useState<ImportPrimaryClientsData | null>(null);
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | null>(null);

  const { importFile, isBusy } = useClientsFileImport();

  const {
    file,
    isDragging,
    isMaxFileSizeError,
    isAllowedExtensionError,
    reset: resetFileDrop,
    inputRefProps,
    dropZoneProps,
  } = useFileDrop({
    allowedExtensions: acceptedExtensions,
    maxFileSize: MAX_FILE_SIZE_BYTES,
    maxFilesCount: 1,
    isMultipleFiles: false,
  });

  const resetUploadError = useCallback(() => setUploadErrorMessage(null), []);

  const wrappedDropZoneProps = {
    ...dropZoneProps,
    onDragOver: (event: React.DragEvent<HTMLDivElement>) => {
      resetUploadError();
      dropZoneProps.onDragOver(event);
    },
  };

  const wrappedInputRefProps = {
    ...inputRefProps,
    onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
      resetUploadError();
      inputRefProps.onChange(event);
    },
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = useCallback(() => {
    resetFileDrop();
    clearFileInput();
    setSelectedAgent(null);
    setImportType(IMPORT_TYPE_OPTIONS[0]);
    setImportSource(IMPORT_SOURCE_OPTIONS[0]);
    setResults(null);
    setUploadErrorMessage(null);
    onClose();
  }, [onClose, resetFileDrop]);

  const handleSave = useCallback(async () => {
    if (!file) return;

    setUploadErrorMessage(null);
    try {
      const agentId = selectedAgent?.value ? Number(selectedAgent.value) : undefined;
      const data = await importFile({
        file,
        agentId,
        importSource: importSource.value ?? undefined,
        importType: importType.value ?? undefined,
      });
      setResults(data);
    } catch (err) {
      const error = err as ErrorResponse<unknown>;
      const details = Array.isArray(error?.errors?.details) ? error.errors.details.join(', ') : error?.errors?.details;
      setUploadErrorMessage(details || getParsedError(err, error?.status) || 'Upload failed. Please try again.');
    }
  }, [file, importFile, selectedAgent, importSource, importType]);

  const dropZoneHint = getDropZoneHint({
    isAllowedExtensionError,
    isMaxFileSizeError,
    uploadErrorMessage,
    acceptedTypesLabel,
  });

  if (results) {
    return (
      <Modal open={isOpen} onClose={handleClose}>
        <ResultsStep results={results} onClose={handleClose} />
      </Modal>
    );
  }

  return (
    <Modal open={isOpen} onClose={handleClose} variant="fullScreenMobile">
      <div className="w-[calc(100vw-2rem)] max-w-150 max-h-[calc(100dvh-2rem)] px-4 py-8 flex flex-col gap-9 md:w-150 md:max-h-none md:px-9 md:py-12 max-md:h-full max-md:w-full max-md:max-h-none max-md:gap-0 max-md:px-0 max-md:py-0 max-md:pt-1.5">
        <div className="hidden px-4.5 py-1 max-md:block">
          <ModalMobileHeader title="Import Clients File" onBack={handleClose} />
        </div>

        <div className="scrollbar-hide flex min-h-0 flex-1 flex-col gap-9 overflow-y-auto md:flex-none md:overflow-visible max-md:gap-5 max-md:px-4 max-md:pb-6 max-md:pt-4">
          <div className="flex flex-col gap-2 text-center max-md:hidden">
            <h2 className="light text-blue-900">Import clients file</h2>
            <p className="text-body-regular text-grey-700">
              Upload a file to bulk import client records into the system.
            </p>
          </div>

          <p className="hidden font-inter text-grey-700 max-md:block max-md:text-sm max-md:leading-normal">
            Upload a file to bulk import client records into the system.
          </p>

          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 md:flex-row">
              <AgentSelector
                className="flex-1"
                label="Agent name"
                placeholder="Select agent"
                value={selectedAgent}
                onChange={setSelectedAgent}
                clearOptionLabel="Select agent"
                disabled={isBusy}
              />

              <Selector<ImportClientType>
                className="flex-1"
                label="Import type"
                options={IMPORT_TYPE_OPTIONS}
                value={importType?.value ?? null}
                onChange={(option) => option && setImportType(option)}
                isClearOption={false}
                isSearchable={false}
                disabled={isBusy}
              />
            </div>

            <Selector<ImportClientSource>
              label="Import source"
              options={IMPORT_SOURCE_OPTIONS}
              value={importSource?.value ?? null}
              onChange={(option) => option && setImportSource(option)}
              isClearOption={false}
              isSearchable={false}
              disabled={isBusy}
            />

            <DropZoneArea
              isDragging={isDragging}
              isError={isMaxFileSizeError || isAllowedExtensionError || !!uploadErrorMessage}
              uploadIcon={FileIcon}
              className="px-6 py-9 max-md:rounded-[14px]"
              disabled={isBusy}
              {...wrappedDropZoneProps}
            >
              <div className="flex flex-col items-center gap-2.5 w-full mt-5">
                {file ? (
                  <div className="flex items-center gap-2">
                    <span className="text-base font-medium text-black font-inter max-md:text-sm max-md:leading-[140%]">
                      {file.name}
                    </span>
                    <IconButton
                      type="button"
                      aria-label="Remove file"
                      color="danger"
                      size="small"
                      disabled={isBusy}
                      onClick={() => {
                        resetFileDrop();
                        clearFileInput();
                        resetUploadError();
                      }}
                      icon={<TrashIcon className="w-4 h-4 shrink-0" />}
                    />
                  </div>
                ) : (
                  <span className="text-base font-medium text-black font-inter text-center max-md:text-sm max-md:leading-[140%]">
                    Drag your file(s) to start uploading
                  </span>
                )}

                {!file && (
                  <>
                    <div className="flex items-center gap-3 w-full">
                      <div className="h-px flex-1 bg-grey-400" />
                      <span className="text-body-small text-grey-600">or</span>
                      <div className="h-px flex-1 bg-grey-400" />
                    </div>

                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="flex items-center gap-2 px-1.5 py-1 text-blue-600 font-medium font-inter text-base hover:opacity-80 transition-opacity max-md:text-sm max-md:leading-[140%] max-md:text-blue-300"
                    >
                      <UploadCloudIcon className="w-4 h-4 shrink-0" />
                      Upload Files
                    </button>
                  </>
                )}

                <p
                  className={cn('text-body-small text-center', dropZoneHint.isError ? 'text-red-500' : 'text-grey-600')}
                >
                  {dropZoneHint.text}
                </p>
              </div>
            </DropZoneArea>
          </div>
        </div>

        <div className="flex shrink-0 gap-5 max-md:gap-4 max-md:px-4 max-md:pb-10">
          <BaseButton
            intent="secondary"
            size="full"
            onClick={handleClose}
            disabled={isBusy}
            className="max-md:text-sm max-md:leading-[140%]"
          >
            Cancel
          </BaseButton>
          <BaseButton
            intent="primary"
            size="full"
            onClick={handleSave}
            disabled={!file || isBusy}
            className="max-md:text-sm max-md:leading-[140%]"
          >
            {isBusy ? 'Saving...' : 'Save'}
          </BaseButton>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        className="sr-only"
        accept={accept}
        aria-hidden
        tabIndex={-1}
        {...wrappedInputRefProps}
      />
    </Modal>
  );
};
